import { useState } from 'react';
import type { JourneyPayload, OuterPayload } from '../../../types/analyticstypes';
import { formatDuration, copyToClipboard, statusColor } from '../../../utils/analytics/format';

function CopyChip({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        await copyToClipboard(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] bg-gray-100 hover:bg-gray-200 text-gray-700 font-mono transition-colors"
      title={`${label}: ${value}`}
    >
      <span className="text-gray-500 font-sans">{label}</span>
      <span className="truncate max-w-[180px]">{value}</span>
      {copied ? (
        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )}
    </button>
  );
}

function StatusPill({ outer }: { outer: OuterPayload }) {
  if (outer.status === 'COMPLETED' && outer.paymentStatus === 'PAID') {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
        ✓ PAID
      </span>
    );
  }
  if (outer.status === 'COMPLETED') {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
        COMPLETED · {outer.paymentStatus}
      </span>
    );
  }
  if (outer.status === 'ABANDONED') {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
        ABANDONED
      </span>
    );
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 ${statusColor(outer.status)}`}>
      {outer.status}
    </span>
  );
}

function RatingStars({ rating, max }: { rating: number; max: number }) {
  return (
    <span className="inline-flex items-center" title={`${rating} of ${max}`}>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} className={i < rating ? 'text-yellow-500' : 'text-gray-300'}>
          ★
        </span>
      ))}
    </span>
  );
}

export default function SessionHeader({
  journey,
  outer,
}: {
  journey: JourneyPayload;
  outer: OuterPayload;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {outer.orderNumber && (
          <span className="text-base font-bold text-gray-900">Order #{outer.orderNumber}</span>
        )}
        <StatusPill outer={outer} />
        {outer.feedbackRating != null && outer.feedbackMaxRating != null && (
          <RatingStars rating={outer.feedbackRating} max={outer.feedbackMaxRating} />
        )}
        {outer.bagRequested && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-amber-50 text-amber-800 border border-amber-200">
            🛍 Bag
          </span>
        )}
        {outer.loyaltyCustomer && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-purple-50 text-purple-800 border border-purple-200">
            ⭐ Loyalty
          </span>
        )}
        {outer.loyaltyEnrollAttempted && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-indigo-50 text-indigo-800 border border-indigo-200">
            ✎ Enroll attempted
          </span>
        )}
        <span className="text-xs text-gray-500">
          {outer.deviceName || outer.deviceId}
          {outer.source ? ` · ${outer.source}` : ''}
          {journey.entryType ? ` · ${journey.entryType} entry` : ''}
          {` · ${formatDuration(journey.sessionDurationMs)}`}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {(outer.ngrpOrderUuid || journey.financials?.ngrpOrderUuid) && (
            <CopyChip
              label="NGRP"
              value={outer.ngrpOrderUuid || journey.financials?.ngrpOrderUuid || ''}
            />
          )}
          {outer.basketGuid && <CopyChip label="Basket" value={outer.basketGuid} />}
        </div>
      </div>
    </div>
  );
}
